// frontend/src/components/DocumentUpload.js - Updated
import React, { useState, useEffect } from 'react';
import documentService from '../services/document/documentService';
import titleService from '../services/title/titleService';
import '../styles/global.css';
import './DocumentUpload.css';

const DocumentUpload = ({ projectId, onUpload }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fileHover, setFileHover] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Auto-dismiss notifications
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleFileDrop = async (e) => {
        e.preventDefault();
        setFileHover(false);
        
        if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) {
            return;
        }
        
        const file = e.dataTransfer.files[0];
        
        // Check file extension
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!['txt', 'md', 'tex'].includes(fileExt)) {
            setError('Only .txt, .md, and .tex files are allowed');
            return;
        }
        
        // Check file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            setError('File size exceeds 10MB limit');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            // Read file content for title generation
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const fileContent = event.target.result;
                    
                    // Extract first 5 paragraphs for title generation
                    const paragraphs = fileContent.split(/\n\s*\n/).slice(0, 5).join('\n\n');
                    
                    // Generate title using title service
                    const title = await titleService.generateTitle(paragraphs);
                    
                    // Create a FormData object with the file and generated title
                    const formData = new FormData();
                    formData.append('title', title);
                    formData.append('file', file);
                    
                    // Upload document using document service
                    const document = await documentService.createDocument(projectId, formData);
                    
                    // Show success message
                    setSuccessMessage(`Document "${title}" uploaded successfully`);
                    
                    // Call the callback with the new document
                    if (onUpload && document) {
                        onUpload(document);
                    }
                } catch (err) {
                    console.error("Title generation or upload error:", err);
                    setError(err.message || "Upload failed. Please try again.");
                } finally {
                    setLoading(false);
                }
            };
            
            reader.onerror = () => {
                setError("Failed to read file");
                setLoading(false);
            };
            
            reader.readAsText(file, 'UTF-8');
            
        } catch (err) {
            console.error("File processing error:", err);
            setError("Failed to process file");
            setLoading(false);
        }
    };

    return (
        <div className="upload-container">
            {error && (
                <div className="upload-alert error">
                    <div className="upload-alert-content">
                        <svg className="upload-alert-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="upload-alert-text">
                            <div className="upload-alert-title">Error</div>
                            <div className="upload-alert-message">{error}</div>
                        </div>
                    </div>
                </div>
            )}
            
            {successMessage && (
                <div className="upload-alert success">
                    <div className="upload-alert-content">
                        <svg className="upload-alert-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <div className="upload-alert-text">
                            <div className="upload-alert-title">Success</div>
                            <div className="upload-alert-message">{successMessage}</div>
                        </div>
                    </div>
                </div>
            )}
            
            <div 
                className={`drop-zone ${fileHover ? 'active' : ''} ${loading ? 'loading' : ''}`}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!loading) setFileHover(true);
                }}
                onDragEnter={(e) => {
                    e.preventDefault();
                    if (!loading) setFileHover(true);
                }}
                onDragLeave={() => {
                    if (!loading) setFileHover(false);
                }}
                onDrop={!loading ? handleFileDrop : (e) => e.preventDefault()}
            >
                <div className="drop-zone-content">
                    {loading ? (
                        <>
                            <svg className="upload-icon loading spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div className="upload-text">Processing document...</div>
                        </>
                    ) : (
                        <>
                            <svg className="upload-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <div className="upload-text">Drag & drop your file here</div>
                            <div className="upload-subtext">
                                Accepted formats: <span className="format-badge">.txt</span> <span className="format-badge">.md</span> <span className="format-badge">.tex</span>
                                <br />Maximum size: 10MB
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentUpload;